/**
 * Softwise M10b — the ONE line generator (pure, type-only imports).
 *
 *   `{heroSeat} {hole} · {seat} {action} {size} · … · 플롭 {3 cards} · …`
 *   e.g. `BTN A♠K♠ · BTN 레이즈 2.5bb · BB 콜 · 플롭 Q♠J♠T♠ · BB 체크 · BTN 벳 3bb`
 *
 * Rules: a raise always uses the raise label (never an "open" label); calls carry no amount; unrecorded
 * parts are omitted (no `?`, no blanks); not-involved seats are omitted
 * (legacy preflop folds before the first entrant too); no pot / evaluation /
 * probability. Input stacked line, Share card, Copy and PNG all call this.
 */
import type { ActionType, Card, HandState, Street, StreetAction } from './types'

export type LineLabels = {
  street: Record<Street, string>
  action: Record<ActionType, string>
}

export type LineSegment =
  /** Softwise M10c: `seat` / `hole` let the input strip tap each part. */
  | { kind: 'hero'; text: string; seat: string; hole: string }
  | { kind: 'action'; text: string; actionId: string; street: Street }
  | { kind: 'board'; text: string; street: 'flop' | 'turn' | 'river' }

export const LINE_SEPARATOR = ' · '

const STREETS: Street[] = ['preflop', 'flop', 'turn', 'river']
const SUIT: Record<string, string> = { s: '♠', h: '♥', d: '♦', c: '♣' }

export function lineCard(card: Card): string {
  return `${card[0]}${SUIT[card[1]!] ?? card[1]}`
}

function lineCards(cards: (Card | null)[]): string {
  return cards.filter((c): c is Card => Boolean(c)).map(lineCard).join('')
}

function lineSeat(actor: string, hand: HandState): string {
  const trimmed = actor.trim()
  if (trimmed.toLowerCase() === 'hero') return hand.heroPosition
  return trimmed
}

/** Legacy `%` sizes render as a pot fraction label — never a bare `%`. */
function lineSize(size: string): string {
  const m = size.trim().match(/^(\d+(?:\.\d+)?)%$/)
  if (!m) return size.trim()
  const n = Number(m[1]) / 100
  return `${Math.round(n * 100) / 100}× pot`
}

export function lineActionText(
  a: StreetAction,
  hand: HandState,
  labels: LineLabels,
): string {
  const seat = lineSeat(a.actor, hand)
  const verb = labels.action[a.type] ?? a.type
  const sized = a.type === 'bet' || a.type === 'raise' || a.type === 'allin'
  const size = sized && a.size?.trim() ? ` ${lineSize(a.size)}` : ''
  return `${seat} ${verb}${size}`
}

/** Preflop folds logged before anyone entered = not involved (legacy macros). */
function notInvolvedLegacyFolds(hand: HandState): Set<string> {
  const skip = new Set<string>()
  for (const a of hand.actions) {
    if (a.street !== 'preflop') continue
    if (a.type !== 'fold') break
    skip.add(a.id)
  }
  return skip
}

function boardSegment(
  hand: HandState,
  street: 'flop' | 'turn' | 'river',
  labels: LineLabels,
): LineSegment | null {
  const cards =
    street === 'flop'
      ? lineCards(hand.board.flop)
      : street === 'turn'
        ? lineCards([hand.board.turn])
        : lineCards([hand.board.river])
  if (!cards) return null
  return { kind: 'board', street, text: `${labels.street[street]} ${cards}` }
}

function num(n: number): string {
  return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '?'
}

/**
 * Softwise M10d — THE stack / settings notation (one place).
 *
 *   bb unit (default)       → `100bb`
 *   chips unit (explicit)   → `20000 칩` (the chip value the user typed —
 *                             `chipUnit` is the localized word; no ≈ bb)
 *
 * No chip conversion is ever shown next to a bb stack. Input stacked line,
 * Share card, Copy (human + pro block) and PNG all call this.
 */
export function formatStackNotation(
  hand: Pick<HandState, 'heroStackBb' | 'heroStackChips' | 'stackUnit'>,
  chipUnit = 'chips',
): string {
  if (
    hand.stackUnit === 'chips' &&
    Number.isFinite(hand.heroStackChips) &&
    hand.heroStackChips > 0
  ) {
    return `${num(hand.heroStackChips)} ${chipUnit}`
  }
  return Number.isFinite(hand.heroStackBb) ? `${num(hand.heroStackBb)}bb` : ''
}

/** Softwise M10d — THE blinds notation: `0.5/1` (`0.5/1 (ante 0.1)` when set). */
export function formatBlindsNotation(
  hand: Pick<HandState, 'smallBlind' | 'bigBlind' | 'ante'>,
): string {
  const base = `${num(hand.smallBlind)}/${num(hand.bigBlind)}`
  if (hand.ante != null && Number.isFinite(hand.ante) && hand.ante > 0) {
    return `${base} (ante ${num(hand.ante)})`
  }
  return base
}

/**
 * Softwise M10c: the quiet defaults segment on the input stacked line —
 * `6명 · 0.5/1 · 100bb` (table · blinds · stack). Input-only (tap → settings);
 * not part of the hand line, so Share / Copy / PNG text is unchanged.
 * Softwise M10d: blinds / stack come from the single notation formatters.
 */
export function lineSettingsText(
  hand: HandState,
  playersUnit: (n: number) => string,
  chipUnit = 'chips',
): string {
  return [playersUnit(hand.playerCount), formatBlindsNotation(hand), formatStackNotation(hand, chipUnit)]
    .filter(Boolean)
    .join(LINE_SEPARATOR)
}

export function buildLineSegments(
  hand: HandState,
  labels: LineLabels,
  opts: { seatPicked?: boolean } = {},
): LineSegment[] {
  const segs: LineSegment[] = []
  const hole = lineCards(hand.holeCards)
  // Softwise M10c: hero seat shows as soon as it is picked (`BTN`), then
  // `BTN A♠`, `BTN A♠K♠`. Share / Copy / PNG keep the hole-cards rule.
  if (hole || opts.seatPicked) {
    segs.push({
      kind: 'hero',
      text: hole ? `${hand.heroPosition} ${hole}` : hand.heroPosition,
      seat: hand.heroPosition,
      hole,
    })
  }
  const skip = notInvolvedLegacyFolds(hand)
  for (const street of STREETS) {
    if (street !== 'preflop') {
      const b = boardSegment(hand, street, labels)
      if (b) segs.push(b)
    }
    for (const a of hand.actions) {
      if (a.street !== street || skip.has(a.id)) continue
      segs.push({
        kind: 'action',
        street,
        actionId: a.id,
        text: lineActionText(a, hand, labels),
      })
    }
  }
  return segs
}


export function formatLineText(hand: HandState, labels: LineLabels): string {
  return buildLineSegments(hand, labels)
    .map((s) => s.text)
    .join(LINE_SEPARATOR)
}
